// src/views/PogeView.jsx
import React from 'react';
import { Shield, Zap, TrendingDown, Lock, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatBalance } from '../utils/formatters';
import { CONFIG } from '../config/constants';

const GUARANTEE_ICONS = [Shield, Lock, Zap, Shield, TrendingDown, Lock];

export const PogeView = ({
  isConnected,
  connectWallet,
  pogeBalance,
  pendingRewards,
  stakedTokens,
  emissionRate,
  setView,
}) => {
  const { t } = useTranslation();

  const dailyEarnings = parseFloat(emissionRate || 0) * (stakedTokens?.length || 0) * 86400;
  const steps        = t('poge.steps',            { returnObjects: true });
  const timeline     = t('poge.emissionTimeline', { returnObjects: true });
  const guarantees   = t('poge.guarantees',       { returnObjects: true });
  const facts        = t('poge.facts',            { returnObjects: true });

  const FACT_ROWS = [
    { label: facts.name,       value: facts.nameValue,       sub: facts.standardValue  },
    { label: facts.symbol,     value: facts.symbolValue,     sub: facts.standardValue  },
    { label: facts.supply,     value: facts.supplyValue,     sub: facts.supplyNote     },
    { label: facts.allocation, value: facts.allocationValue, sub: facts.allocationNote },
    { label: facts.publicSale, value: facts.publicSaleValue, sub: facts.publicSaleNote },
    { label: facts.teamTokens, value: facts.teamTokensValue, sub: facts.teamTokensNote },
    { label: facts.emission,   value: facts.emissionValue,   sub: facts.emissionNote   },
    { label: facts.multiplier, value: facts.multiplierValue, sub: facts.multiplierNote },
  ];

  return (
    <div>
      {/* PAGE TITLE */}
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <h2 className="text-3xl md:text-5xl font-black text-white uppercase font-mono">
          {t('poge.title')}
        </h2>
        <span className="bg-yellow-300 text-black font-black text-sm px-3 py-1 border-2 border-black font-mono">
          {t('poge.symbol')}
        </span>
        <span className="bg-black text-green-400 font-black text-xs px-3 py-1 border-2 border-green-400 font-mono uppercase">
          {t('poge.neverSold')}
        </span>
      </div>

      <div className="space-y-8">

        {/* PERSONAL STATS — wallet connected */}
        {isConnected && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-yellow-300 text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000]">
              <div className="text-xs font-black uppercase font-mono opacity-60 mb-1">{t('poge.balanceLabel')}</div>
              <div className="text-4xl font-black font-mono">{formatBalance(pogeBalance)}</div>
              <div className="text-sm font-bold mt-1 opacity-70">{t('poge.symbol')}</div>
            </div>
            <div className="bg-green-400 text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000]">
              <div className="text-xs font-black uppercase font-mono opacity-60 mb-1">{t('poge.pendingLabel')}</div>
              <div className="text-4xl font-black font-mono">{parseFloat(pendingRewards || 0).toFixed(4)}</div>
              <div className="text-sm font-bold mt-1 opacity-70">{t('poge.pendingUnit')}</div>
            </div>
            <div className="bg-black text-white p-6 border-4 border-white shadow-[6px_6px_0_0_rgba(255,255,255,0.3)]">
              <div className="text-xs font-black uppercase font-mono text-green-400 mb-1">{t('poge.dailyLabel')}</div>
              <div className="text-4xl font-black font-mono text-yellow-300">
                +{dailyEarnings.toFixed(2)}
              </div>
              <div className="text-sm font-bold mt-1 opacity-50">
                {t('rewards.stakedLabel', { staked: stakedTokens?.length || 0 })}
              </div>
            </div>
          </div>
        )}

        {/* NOT CONNECTED PROMPT */}
        {!isConnected && (
          <div className="bg-black border-4 border-yellow-300 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <div className="text-yellow-300 font-black uppercase font-mono text-sm mb-1">
                {t('poge.connectPromptTitle')}
              </div>
              <div className="text-white font-mono text-sm opacity-70">
                {t('poge.connectPromptDesc')}
              </div>
            </div>
            <button
              onClick={connectWallet}
              className="bg-yellow-300 text-black font-black px-8 py-3 border-4 border-black shadow-[4px_4px_0_0_#000] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#000] transition-all uppercase font-mono whitespace-nowrap flex items-center gap-2"
            >
              {t('common.connectWallet')} <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* TOKEN FACTS */}
        <div>
          <h3 className="text-xl font-black uppercase font-mono text-white border-b-4 border-white pb-2 mb-4">
            {t('poge.sectionTokenFacts')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {FACT_ROWS.map(({ label, value, sub }) => (
              <div key={label} className="bg-black border-l-4 border-yellow-300 border-t border-r border-b border-white/10 p-4">
                <div className="text-xs text-white/40 uppercase font-mono mb-1">{label}</div>
                <div className="text-lg font-black text-white font-mono">{value}</div>
                <div className="text-xs text-white/40 font-mono mt-0.5">{sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CONTRACT ADDRESS */}
        <div className="bg-black border-4 border-green-400 p-5">
          <div className="text-xs text-green-400 uppercase font-mono font-black mb-2">
            {t('poge.contractLabel')}
          </div>
          <div className="text-white font-mono text-sm break-all mb-3">{CONFIG.ADDRESSES.COIN}</div>
          <a
            href={`https://etherscan.io/token/${CONFIG.ADDRESSES.COIN}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-400 text-black font-black text-xs px-4 py-2 border-2 border-black uppercase font-mono hover:bg-yellow-300 transition-colors"
          >
            {t('poge.viewOnEtherscan')} <ArrowRight size={12} />
          </a>
        </div>

        {/* HOW TO EARN */}
        <div>
          <h3 className="text-xl font-black uppercase font-mono text-white border-b-4 border-white pb-2 mb-4">
            {t('poge.sectionHowToEarn')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {steps.map(({ n, title, desc }) => (
              <div key={n} className="bg-black border-2 border-white/20 border-t-4 border-t-yellow-300 p-5">
                <div className="text-3xl font-black text-yellow-300 font-mono mb-2">{n}</div>
                <div className="text-sm font-black text-white uppercase font-mono mb-2">{title}</div>
                <div className="text-xs text-white/50 font-mono leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* EMISSION TABLE */}
        <div>
          <h3 className="text-xl font-black uppercase font-mono text-white border-b-4 border-white pb-2 mb-4">
            {t('poge.sectionEmission')}
          </h3>
          <p className="text-sm text-white/50 font-mono mb-4">{t('poge.emissionNote')}</p>
          <div className="border-4 border-white overflow-hidden">
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="bg-yellow-300 text-black">
                  <th className="text-left p-3 font-black uppercase text-xs">{t('poge.emissionTableDay')}</th>
                  <th className="text-left p-3 font-black uppercase text-xs">{t('poge.emissionTableBase')}</th>
                  <th className="text-left p-3 font-black uppercase text-xs">{t('poge.emissionTableWith2x')}</th>
                </tr>
              </thead>
              <tbody>
                {timeline.map(({ day, base, with2x }, i) => (
                  <tr key={day} className={i % 2 === 0 ? 'bg-black' : 'bg-white/5'}>
                    <td className="p-3 text-white/60">{day}</td>
                    <td className="p-3 text-white font-bold">{base}</td>
                    <td className="p-3 text-yellow-300 font-bold">{with2x}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-white/30 font-mono mt-2">{t('poge.emissionFootnote')}</div>
        </div>

        {/* GUARANTEES */}
        <div>
          <h3 className="text-xl font-black uppercase font-mono text-white border-b-4 border-white pb-2 mb-4">
            {t('poge.sectionGuarantees')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {guarantees.map(({ title, desc }, i) => {
              const Icon = GUARANTEE_ICONS[i] || Shield;
              return (
                <div key={title} className="bg-black border border-white/10 border-l-4 border-l-green-400 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} className="text-green-400 shrink-0" />
                    <div className="text-xs font-black text-green-400 uppercase font-mono">{title}</div>
                  </div>
                  <div className="text-xs text-white/50 font-mono leading-relaxed">{desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-yellow-300 border-4 border-black p-8 text-center shadow-[8px_8px_0_0_#000]">
          <div className="text-3xl font-black text-black uppercase font-mono mb-2">
            {t('poge.ctaTitle')}
          </div>
          <div className="text-sm text-black/60 font-mono mb-6">
            {t('poge.ctaDesc')}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setView('stake')}
              className="bg-black text-yellow-300 font-black px-8 py-4 border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,0.3)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all uppercase font-mono flex items-center justify-center gap-2"
            >
              {t('nav.stake')} <ArrowRight size={16} />
            </button>
            <button
              onClick={() => setView('mint')}
              className="bg-white text-black font-black px-8 py-4 border-4 border-black hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all uppercase font-mono"
            >
              {t('nav.mint')}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
